import { createContext, useContext, useState, ReactNode, useEffect } from 'react';
import { Foundation } from '../types/Foundation';

interface SavedFoundationsContextType {
  savedFoundations: Foundation[];
  addFoundation: (foundation: Foundation) => void;
  removeFoundation: (foundationName: string) => void;
  clearFoundations: () => void;
  userEmail: string;
  setUserEmail: (email: string) => void;
  limitReached: boolean;
  maxFoundations: number;
}

const SavedFoundationsContext = createContext<SavedFoundationsContextType | undefined>(undefined);

// Key for localStorage
const STORAGE_KEY = 'savedFoundations';
const EMAIL_KEY = 'userEmail';

// Maximum limit of foundations
const MAX_FOUNDATIONS = 90;

export function SavedFoundationsProvider({ children }: { children: ReactNode }) {
  const [savedFoundations, setSavedFoundations] = useState<Foundation[]>(() => {
    // Initialize from localStorage if available
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : [];
  });
  
  const [userEmail, setUserEmail] = useState(() => {
    return localStorage.getItem(EMAIL_KEY) || '';
  });
  
  // Track whether the limit has been reached
  const [limitReached, setLimitReached] = useState(false);

  // Save to localStorage when state changes
  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(savedFoundations));
    
    // Update limitReached based on current count
    setLimitReached(savedFoundations.length >= MAX_FOUNDATIONS);
  }, [savedFoundations]);

  // Save email to localStorage
  useEffect(() => {
    localStorage.setItem(EMAIL_KEY, userEmail);
  }, [userEmail]);

  // Listen for the saveFoundation custom event
  useEffect(() => {
    const handleSaveFoundation = (event: CustomEvent<Foundation>) => {
      addFoundation(event.detail);
    };

    window.addEventListener('saveFoundation', handleSaveFoundation as EventListener);
    
    return () => {
      window.removeEventListener('saveFoundation', handleSaveFoundation as EventListener);
    };
  }, []);

  const addFoundation = (foundation: Foundation) => {
    setSavedFoundations(prev => {
      // Check if this foundation already exists
      const exists = prev.some(f => f.name === foundation.name);
      if (exists) {
        return prev; // Don't add duplicates
      }
      
      // Check if adding this would exceed the limit
      if (prev.length >= MAX_FOUNDATIONS) {
        // Instead of alert, just set the flag and don't add
        setLimitReached(true);
        
        // Create a custom event to show a notification
        window.dispatchEvent(new CustomEvent('limitReached', { 
          detail: { 
            message: `Du kan endast spara upp till ${MAX_FOUNDATIONS} stiftelser.`,
            count: MAX_FOUNDATIONS
          } 
        }));
        
        return prev; // Don't add more
      }
      
      return [...prev, foundation];
    });
  };

  const removeFoundation = (foundationName: string) => {
    setSavedFoundations(prev => 
      prev.filter(foundation => foundation.name !== foundationName)
    );
    
    // Reset limitReached if we're removing a foundation and were at the limit
    if (limitReached) {
      setLimitReached(false);
    }
  };

  const clearFoundations = () => {
    setSavedFoundations([]);
    setLimitReached(false);
  };

  return (
    <SavedFoundationsContext.Provider 
      value={{
        savedFoundations,
        addFoundation,
        removeFoundation,
        clearFoundations,
        userEmail,
        setUserEmail,
        limitReached,
        maxFoundations: MAX_FOUNDATIONS
      }}
    >
      {children}
    </SavedFoundationsContext.Provider>
  );
}

export function useSavedFoundations() {
  const context = useContext(SavedFoundationsContext);
  if (context === undefined) {
    throw new Error('useSavedFoundations must be used within a SavedFoundationsProvider');
  }
  return context;
}
